// Çek mətninin tərtibi (58/80 mm — monospace sətirlər). Çap CSS-i və sətir uzunluğu buradan.
import type { ReceiptData } from '@kassa/shared';
import { formatMoney, formatQty, UNIT_LABEL } from './format';

export type Paper = '58' | '80' | 'A4';

export const PAPER_KEY = 'kassa.kagiz';
export const AUTOPRINT_KEY = 'kassa.avtoCap';

/** Kağız enindən sətirdə simvol sayı (monospace, 12 pt-yə yaxın). */
export function paperChars(paper: Paper): number {
  return paper === '58' ? 32 : 48;
}

/** Uzun mətn kəsilir, sonda «…» qoyulur. */
export function truncate(text: string, max: number): string {
  if (max <= 0) return '';
  return text.length > max ? `${text.slice(0, Math.max(0, max - 1))}…` : text;
}

/** Sol və sağ mətn bir sətirdə: sağ tərəf (məbləğ) tam, sol tərəf kəsilir. */
export function row(left: string, right: string, width: number): string {
  const rightPart = right;
  const leftMax = Math.max(0, width - rightPart.length - 1);
  const l = truncate(left, leftMax);
  return `${l}${' '.repeat(Math.max(1, width - l.length - rightPart.length))}${rightPart}`;
}

/** Çek sətirləri (monospace). copy = true olanda TƏKRAR işarəsi. */
export function receiptRows(d: ReceiptData, width: number): string[] {
  const out: string[] = [];
  const center = (s: string) => ' '.repeat(Math.max(0, Math.floor((width - s.length) / 2))) + s;
  out.push(center(truncate(d.company.name, width)));
  if (d.company.voen) out.push(center(`VÖEN: ${d.company.voen}`));
  if (d.company.address) out.push(center(truncate(d.company.address, width)));
  out.push('-'.repeat(width));
  out.push(truncate(`${d.kind === 'QAY' ? 'Qaytarma' : d.kind === 'NSY' ? 'Nisyə ödənişi' : 'Satış'} ${d.number}`, width));
  if (d.copy) out.push(center('*** TƏKRAR ***'));
  out.push(truncate(`${d.outlet} · ${d.register}`, width));
  out.push(truncate(`Kassir: ${d.cashier}`, width));
  out.push(truncate(new Date(d.dateTime).toLocaleString('az-AZ'), width));
  out.push('-'.repeat(width));
  for (const l of d.lines) {
    out.push(truncate(l.name, width));
    out.push(row(`  ${formatQty(l.qty)} ${UNIT_LABEL[l.unit] ?? l.unit} × ${formatMoney(l.unitPrice).replace(' ₼', '')}${l.discount ? ` −${formatMoney(l.discount).replace(' ₼', '')}` : ''}`, formatMoney(l.amount).replace(' ₼', ''), width));
  }
  out.push('-'.repeat(width));
  if (d.vatPayer) out.push(row('ƏDV daxil', formatMoney(d.vatAmount).replace(' ₼', ''), width));
  out.push(row('CƏM', formatMoney(d.total), width));
  if (d.payments.cash > 0) out.push(row('Nağd', formatMoney(d.payments.cash), width));
  if (d.payments.card > 0) out.push(row('Kart', formatMoney(d.payments.card), width));
  if (d.payments.credit > 0) out.push(row('Nisyə', formatMoney(d.payments.credit), width));
  if (d.cashGiven !== null && d.change !== null) {
    out.push(row('Verilən', formatMoney(d.cashGiven), width));
    out.push(row('Qalıq pul', formatMoney(d.change), width));
  }
  if (d.customer) {
    out.push('-'.repeat(width));
    out.push(truncate(`Müştəri: ${d.customer.name}`, width));
    out.push(row('Borc qalığı', formatMoney(d.customer.balance), width));
  }
  out.push('-'.repeat(width));
  out.push(center(d.footer.slice(0, width)));
  return out;
}

/** Çap CSS-i: kağız eni və kənar boşluq. */
export function printCss(paper: Paper): string {
  const size = paper === 'A4' ? 'A4' : `${paper}mm auto`;
  return `@page { size: ${size}; margin: ${paper === 'A4' ? '15mm' : '2mm'}; }`;
}
